import { apiOutletClient } from "@/features/shared";

const INVENTORY_VALUE_REPORT_PATH = "reports/inventory-value";
const REPORT_CLIENT_TIMEOUT_MS = 45000;
const SUPPORTED_REPORT_SCHEMA_VERSION = 1;

const REQUIRED_METADATA_HEADERS = [
  "x-report-schema-version",
  "x-report-id",
  "x-report-generated-at",
  "x-report-row-count",
  "x-report-has-invalid",
];

const STATUS_MESSAGES = {
  400: "Permintaan laporan tidak valid.",
  401: "Sesi Anda telah berakhir. Silakan masuk kembali.",
  403: "Anda tidak memiliki akses ke laporan, outlet, atau gudang ini.",
  404: "Outlet tidak ditemukan.",
  422: "Laporan terlalu besar. Persempit filter lalu coba lagi.",
  503: "Laporan melebihi batas waktu. Persempit filter lalu coba lagi.",
};

export class InventoryValueReportRequestError extends Error {
  constructor(
    message,
    { status = null, code = null, requestId = null, details = [] } = {}
  ) {
    super(message);
    this.name = "InventoryValueReportRequestError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

const parseJson = (value) => {
  if (typeof value !== "string") return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/**
 * Reads both the report error envelope (`{ meta: { error, error_code } }`) and the
 * global exception filter format (`{ statusCode, message }`). Text responses are not
 * camelized by the shared client, JSON ones are, so both key styles are accepted.
 */
const parseReportErrorBody = (data) => {
  const body = parseJson(data);
  if (!body || typeof body !== "object") return {};

  const meta = body.meta ?? {};
  const message =
    meta.error ??
    (Array.isArray(body.message) ? body.message.join(", ") : body.message);

  return {
    message: typeof message === "string" ? message : null,
    code: meta.error_code ?? meta.errorCode ?? null,
    requestId: meta.request_id ?? meta.requestId ?? null,
    details: Array.isArray(meta.details) ? meta.details : [],
  };
};

export const isReportRequestCanceled = (error) =>
  error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

const toReportRequestError = (error) => {
  if (
    error instanceof InventoryValueReportRequestError ||
    isReportRequestCanceled(error)
  ) {
    return error;
  }

  const response = error?.response;
  if (!response) {
    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return new InventoryValueReportRequestError(
        "Permintaan laporan melebihi batas waktu. Persempit filter lalu coba lagi.",
        { code: "CLIENT_TIMEOUT" }
      );
    }
    return new InventoryValueReportRequestError(
      "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.",
      { code: "NETWORK_ERROR" }
    );
  }

  const parsed = parseReportErrorBody(response.data);
  // Report errors carry a stable code and a user-facing message; guard errors
  // (authentication, platform, outlet) do not, so they get a local message.
  const message = parsed.code
    ? parsed.message
    : STATUS_MESSAGES[response.status] ?? parsed.message;

  return new InventoryValueReportRequestError(
    message ?? "Laporan gagal dimuat. Silakan coba lagi.",
    {
      status: response.status,
      code: parsed.code,
      requestId: parsed.requestId,
      details: parsed.details,
    }
  );
};

const headerValue = (headers, name) => {
  if (!headers) return undefined;
  const value =
    typeof headers.get === "function" ? headers.get(name) : headers[name];
  return value === undefined || value === null ? undefined : String(value);
};

/**
 * Accepts only a genuine report document. Anything else — a JSON body, a login page
 * or a response without report metadata — becomes an error and never reaches an iframe.
 */
export const readReportDocument = (response) => {
  // The shared client resolves `undefined` after logging out an expired session.
  if (!response) {
    throw new InventoryValueReportRequestError(STATUS_MESSAGES[401], {
      status: 401,
    });
  }

  const contentType = headerValue(response.headers, "content-type") ?? "";
  if (
    response.status !== 200 ||
    !contentType.toLowerCase().startsWith("text/html") ||
    typeof response.data !== "string"
  ) {
    throw new InventoryValueReportRequestError(
      "Server mengirim respons yang bukan dokumen laporan.",
      { status: response.status, code: "INVALID_REPORT_RESPONSE" }
    );
  }

  const schemaVersion = Number(
    headerValue(response.headers, "x-report-schema-version")
  );
  const rowCount = Number(headerValue(response.headers, "x-report-row-count"));
  const hasInvalid = headerValue(response.headers, "x-report-has-invalid");
  const missing = REQUIRED_METADATA_HEADERS.filter(
    (name) => headerValue(response.headers, name) === undefined
  );

  if (
    missing.length > 0 ||
    schemaVersion !== SUPPORTED_REPORT_SCHEMA_VERSION ||
    !Number.isInteger(rowCount) ||
    rowCount < 0 ||
    !["true", "false"].includes(hasInvalid)
  ) {
    throw new InventoryValueReportRequestError(
      "Metadata laporan tidak lengkap atau versinya tidak didukung.",
      { status: response.status, code: "INVALID_REPORT_RESPONSE" }
    );
  }

  const page = headerValue(response.headers, "x-report-page");
  const pageSize = headerValue(response.headers, "x-report-page-size");

  return {
    html: response.data,
    metadata: {
      schemaVersion,
      reportId: headerValue(response.headers, "x-report-id"),
      generatedAt: headerValue(response.headers, "x-report-generated-at"),
      rowCount,
      hasInvalid: hasInvalid === "true",
      page: page === undefined ? null : Number(page),
      pageSize: pageSize === undefined ? null : Number(pageSize),
    },
  };
};

const requestReportDocument = async (path, { outletId, body, signal }) => {
  try {
    const response = await apiOutletClient.post(path, body, {
      // The captured outlet travels explicitly so a localStorage outlet switch in
      // another tab cannot change the scope of this request.
      headers: { "Outlet-Id": outletId, Accept: "text/html" },
      responseType: "text",
      timeout: REPORT_CLIENT_TIMEOUT_MS,
      signal,
    });
    return readReportDocument(response);
  } catch (error) {
    throw toReportRequestError(error);
  }
};

export const fetchInventoryValueReportPreview = ({ outletId, body, signal }) =>
  requestReportDocument(`${INVENTORY_VALUE_REPORT_PATH}/preview`, {
    outletId,
    body,
    signal,
  });

export const fetchInventoryValueMovementPreview = ({ outletId, body, signal }) =>
  requestReportDocument(`${INVENTORY_VALUE_REPORT_PATH}/movements/preview`, {
    outletId,
    body,
    signal,
  });

export const fetchInventoryValueReportOptions = async ({ outletId, signal }) => {
  try {
    const response = await apiOutletClient.get(
      `${INVENTORY_VALUE_REPORT_PATH}/options`,
      {
        headers: { "Outlet-Id": outletId },
        timeout: REPORT_CLIENT_TIMEOUT_MS,
        signal,
      }
    );
    if (!response) {
      throw new InventoryValueReportRequestError(STATUS_MESSAGES[401], {
        status: 401,
      });
    }

    const options = response.data?.data;
    if (options?.schemaVersion !== SUPPORTED_REPORT_SCHEMA_VERSION) {
      throw new InventoryValueReportRequestError(
        "Versi laporan di server tidak didukung. Muat ulang halaman.",
        { code: "UNSUPPORTED_SCHEMA" }
      );
    }
    return options;
  } catch (error) {
    throw toReportRequestError(error);
  }
};
